import { QueryResource, QueryResources, QueryResourcesInfinite } from 'datx-jsonapi-react';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { ThinEventSession } from '@/resources/ThinEventSession';

export function queryThinEventSession(id: string): QueryResource<ThinEventSession> {
  return id && [ThinEventSession, id];
}

export const queryThinEventSessions = (filters: Record<string, string>): QueryResources<ThinEventSession> => {
  const newFilter = omitBy(filters, isNil);

  return filters.event_id
    ? [
        ThinEventSession,
        {
          filter: newFilter,
          sort: ['starts_at', 'name'],
          params: [
            {
              key: 'page[size]',
              value: '500',
            },
            {
              key: 'page[number]',
              value: '1',
            },
          ],
          loadAll: true,
        },
      ]
    : null;
};

export const queryThinEventSessionsIndexSearch = (
  event_id: string,
  query: string,
  filters: Record<string, string>
): QueryResources<ThinEventSession> => {
  if (!event_id || !query) return null;

  const filter = omitBy({ event_id, query, ...filters }, isNil);

  return [
    ThinEventSession,
    {
      filter,
      sort: ['starts_at', 'name'],
      params: [
        {
          key: 'page[size]',
          value: '4',
        },
        {
          key: 'page[number]',
          value: '1',
        },
      ],
    },
  ];
};

export const queryThinEventSessionsForDay = (
  eventId: string,
  trackId: string,
  day_starts_at: string,
  day_ends_at: string,
  agenda_profile_id?: string
): QueryResources<ThinEventSession> => {
  if (!day_starts_at || !day_ends_at) return null;
  return eventId
    ? [
        ThinEventSession,
        {
          filter: {
            event_id: eventId,
            ...(trackId && { track_id: trackId }),
            day_starts_at,
            day_ends_at,
            ...(agenda_profile_id && { agenda_profile_id }),
          },
          sort: ['starts_at', 'name'],
          params: [
            {
              key: 'page[size]',
              value: '500',
            },
            {
              key: 'page[number]',
              value: '1',
            },
          ],
          loadAll: true,
        },
      ]
    : null;
};

export const querySortedThinEventSessions = (
  event_id: string,
  sortQuery: string,
  time_range_overlap?: boolean
): QueryResources<ThinEventSession> => {
  const filters = {
    event_id,
    time_range_overlap,
  };

  return event_id && sortQuery
    ? [
        ThinEventSession,
        {
          filter: omitBy(filters, isNil),
          sort: sortQuery,
          params: [
            {
              key: 'page[size]',
              value: '1',
            },
            {
              key: 'page[number]',
              value: '1',
            },
          ],
        },
      ]
    : null;
};

interface IQueryEventSessionsByQueryInfiniteProps {
  filters: Record<string, string>;
  pageSize?: number;
}

export const queryThinEventSessionsByQueryInfinite = ({
  filters,
  pageSize = 10,
}: IQueryEventSessionsByQueryInfiniteProps): QueryResourcesInfinite<ThinEventSession> => {
  return (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData?.links?.next) {
      return null;
    }
    const pageNumber = pageIndex + 1;

    return [
      ThinEventSession,
      {
        filter: {
          ...omitBy(filters, isNil),
        },
        sort: ['starts_at', 'name'],
        params: [
          {
            key: 'page[size]',
            value: pageSize.toString(),
          },
          {
            key: 'page[number]',
            value: pageNumber.toString(),
          },
        ],
      },
    ];
  };
};

export const queryThinEventSessionsWithLivestreams = (
  filters: Record<string, string>
): QueryResources<ThinEventSession> => [
  ThinEventSession,
  {
    filter: {
      scope: 'with_livestreams',
      ...omitBy(filters, isNil),
    },
  },
];
